import { Link } from 'react-router-dom'
import {
  TrendingUp,
  Trophy,
  Brain,
  Target,
  Zap,
  BarChart3,
  Users,
  Star,
  ArrowRight,
  Check
} from 'lucide-react'

const Home = () => {
  const features = [
    {
      icon: Brain,
      title: 'AI-Powered Analytics',
      description: 'Advanced algorithms analyze thousands of data points for comprehensive insights'
    },
    {
      icon: TrendingUp,
      title: 'Real-Time Data',
      description: 'Live stats, injury reports, and line movements updated every minute'
    },
    {
      icon: Trophy,
      title: 'Professional Analytics',
      description: 'Deep dive into team performance, player impact, and matchup advantages'
    },
    {
      icon: Zap,
      title: 'Instant Insights',
      description: 'Get comprehensive analysis and key factors for every game'
    }
  ]

  const sports = [
    { name: 'NBA', emoji: '🏀', games: 'Daily Games' },
    { name: 'NFL', emoji: '🏈', games: 'Weekly Matchups' },
    { name: 'MLB', emoji: '⚾', games: '162 Games/Season' },
    { name: 'Soccer', emoji: '⚽', games: 'Global Coverage' }
  ]

  const stats = [
    { value: '99.9%', label: 'Uptime' },
    { value: '10K+', label: 'Games Analyzed' },
    { value: '500+', label: 'Daily Analytics' },
    { value: '4.8', label: 'User Rating' }
  ]

  return (
    <div className="min-h-screen bg-dark-bg">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-600/20 to-purple-600/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Professional <span className="text-primary-500">Sports Analytics</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Comprehensive sports analytics platform powered by AI.
              Get real-time insights, injury reports, and data-driven analysis for NBA, NFL, MLB, and more.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/games"
                className="inline-flex items-center justify-center px-8 py-3 text-lg font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors"
              >
                View Analytics
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/learn"
                className="inline-flex items-center justify-center px-8 py-3 text-lg font-medium text-white bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
              >
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-dark-card border-y border-dark-border py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-3xl font-bold text-primary-500">{stat.value}</p>
                <p className="text-gray-400 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Everything You Need for Smart Analysis
            </h2>
            <p className="text-gray-400 text-lg">
              Professional tools and insights for data-driven sports analysis
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((feature, i) => {
              const Icon = feature.icon
              return (
                <div key={i} className="bg-dark-card border border-dark-border rounded-lg p-6 hover:border-primary-500/50 transition-colors">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-primary-500/20 rounded-lg">
                      <Icon className="h-6 w-6 text-primary-500" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                      <p className="text-gray-400">{feature.description}</p>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Sports Coverage */}
      <div className="bg-dark-card border-y border-dark-border py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Complete Sports Coverage
            </h2>
            <p className="text-gray-400 text-lg">
              Professional analysis across all major sports
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {sports.map((sport, i) => (
              <div key={i} className="bg-gray-800/50 rounded-lg p-6 text-center hover:bg-gray-800 transition-colors">
                <div className="text-4xl mb-3">{sport.emoji}</div>
                <h3 className="text-xl font-bold text-white mb-1">{sport.name}</h3>
                <p className="text-sm text-gray-400">{sport.games}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              How It Works
            </h2>
            <p className="text-gray-400 text-lg">
              Start winning in three simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Browse Games</h3>
              <p className="text-gray-400">
                View today's games with real-time odds and analytics
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Get Insights</h3>
              <p className="text-gray-400">
                AI analyzes stats, injuries, and trends for smart picks
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Track Success</h3>
              <p className="text-gray-400">
                Monitor your predictions and improve your strategy
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="bg-dark-card border-y border-dark-border py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Always Free, Forever
            </h2>
            <p className="text-gray-400 text-lg">
              No credit card required. No hidden fees. Just winning insights.
            </p>
          </div>

          <div className="max-w-2xl mx-auto bg-gradient-to-r from-primary-600/20 to-purple-600/20 border border-primary-500/30 rounded-lg p-8">
            <div className="text-center mb-6">
              <p className="text-5xl font-bold text-white mb-2">$0</p>
              <p className="text-gray-400">Forever Free</p>
            </div>

            <ul className="space-y-3 mb-8">
              {[
                'Real-time game analytics',
                'AI-powered predictions',
                'Injury reports & updates',
                'Historical data access',
                'Learning modules',
                'Performance tracking',
                'All sports coverage',
                'No ads, ever'
              ].map((feature, i) => (
                <li key={i} className="flex items-center gap-3 text-white">
                  <Check className="h-5 w-5 text-green-500" />
                  {feature}
                </li>
              ))}
            </ul>

            <Link
              to="/games"
              className="block w-full text-center px-8 py-3 text-lg font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors"
            >
              Start Using Now - No Signup Required
            </Link>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Analyze Like a Pro?
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Join thousands of sports fans using data to understand the game better
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/games"
              className="inline-flex items-center justify-center px-8 py-3 text-lg font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors"
            >
              View Today's Games
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/analytics"
              className="inline-flex items-center justify-center px-8 py-3 text-lg font-medium text-white bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
            >
              See Analytics Dashboard
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Home